import { useRef, useEffect } from "react";
import ReactPlayer from "react-player/lazy";
import { ArrowLeft } from "@phosphor-icons/react";
import mux from "mux-embed";
import { useAtom } from "jotai";
import { userAtom } from "@/Atoms";

function VideoPlayer({
  url,
  thumbnail,
  onClose,
  playerName = "Video Player",
  videoId,
  videoTitle,
}) {
  const videoRef = useRef();
  const monitoredElementRef = useRef(null);
  const [userState] = useAtom(userAtom);

  // Attach Mux monitoring once ReactPlayer's internal <video> element exists.
  // This component only mounts when playback starts, so onReady is more
  // reliable than an effect, which can run before the element is available.
  const trackVideo = () => {
    const videoElement = videoRef.current?.getInternalPlayer();
    if (!videoElement || !process.env.NEXT_PUBLIC_MUX_KEY) return;

    if (monitoredElementRef.current) {
      mux.destroyMonitor(monitoredElementRef.current);
    }
    monitoredElementRef.current = videoElement;

    const initTime = mux.utils.now();
    mux.monitor(videoElement, {
      debug: false,
      data: {
        env_key: process.env.NEXT_PUBLIC_MUX_KEY, // required
        viewer_user_id: userState?.id,
        viewer_user_email: userState?.email,
        player_name: playerName,
        player_init_time: initTime,
        video_id: videoId || url,
        video_title: videoTitle || url,
      },
    });
  };

  useEffect(() => {
    return () => {
      if (monitoredElementRef.current) {
        mux.destroyMonitor(monitoredElementRef.current);
        monitoredElementRef.current = null;
      }
    };
  }, []);

  const doFullscreen = async function () {
    try {
      const screenfull = await import("screenfull");
      if (screenfull.default && screenfull.default.isEnabled) {
        await screenfull.default.request();
      }
    } catch (error) {
      console.log("Error", error);
    }
  };

  const closeScreen = async () => {
    const screenfull = await import("screenfull");
    if (screenfull.default.isEnabled) {
      screenfull.default.on("change", () => {
        if (!screenfull.default.isFullscreen) {
          onClose();
        }
      });
    }
  };

  useEffect(() => {
    closeScreen();
  }, []);

  useEffect(() => {
    doFullscreen();
    document.body.classList.add("overflow-hidden");

    return async () => {
      try {
        const screenfull = await import("screenfull");
        document.body.classList.remove("overflow-hidden");
        if (screenfull.default.isEnabled && screenfull.default.isFullscreen) {
          await screenfull.default.exit();
        }
      } catch (error) {
        console.log("Error", error);
      }
    };
  }, []);

  return (
    <div className='fixed top-0 left-0 w-full h-full bg-black z-[100]'>
      <span
        className='absolute top-6 left-6 cursor-pointer hover:opacity-60 z-[101]'
        onClick={onClose}
      >
        <ArrowLeft color='white' size={36} />
      </span>
      <ReactPlayer
        ref={videoRef}
        width={"100%"}
        height={"100%"}
        url={url}
        playing={true}
        onReady={trackVideo}
        onPlay={doFullscreen}
        config={{ file: { attributes: { controlsList: "nodownload" } } }}
        controls
        stopOnUnmount={false}
        pip
      />
    </div>
  );
}

export default VideoPlayer;
